import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Checkbox, Radio, Table, Tag } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import { collect } from 'collect.js';
import DateRangePicker, {
  filterFactory,
  filter_this_month,
  variant_dropdown,
} from '../../components/filters/DateRangePicker';
import useApiClient from '../../hooks/api/useApiClient';
import useHelpers from '../../hooks/context/useHelpers';
import useForm from '../../hooks/utility/useForm';
import { BillingMode } from '../../services/exports/Constants';
import CompanySelect from '../../components/form/business/CompanySelect';
import { isSameDateRange } from '../../services/helpers/DateRangeHelpers';

export const BillingTypeOptions = {
  Subscription: 'subscription',
  Manual: 'manual',
};

export default function BillingHealthTab() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:CustomerBase:billing_health_tab' });

  const { CustomerBaseStatsManager } = useApiClient();

  const { formatCurrency, formatDate } = useHelpers();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const { data: requestParams, update: updateRequestParam } = useForm({
    page: data?.current_page || 1,
    billing_type: BillingTypeOptions.Subscription,
    filters: {
      billing_modes: Object.values(BillingMode),
      company_ids: [],
      high_risk_only: false,
      ...filterFactory(filter_this_month),
    },
  });

  useEffect(() => {
    fetchData(requestParams.page);
  }, [requestParams]);

  const fetchData = async (page) => {
    setLoading(true);
    const { data, success } = await CustomerBaseStatsManager.getBillingHealthStats({
      ...requestParams,
      page,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setData(data);
  };

  const columns = collect([
    {
      title: 'ID',
      key: 'id',
      dataIndex: 'company_id',
    },
    {
      title: t('fields.name'),
      key: 'name',
      render: (item) => (
        <>
          {item.is_high_billing_risk_customer && (
            <Tag color="warning" icon={<ExclamationCircleOutlined />}>
              {t('tags.is_high_billing_risk_customer')}
            </Tag>
          )}{' '}
          {item.name}
        </>
      ),
    },
    {
      title: t('fields.live_from'),
      key: 'live_from',
      render: (item) => formatDate(item.live_from),
    },
    requestParams.billing_type === BillingTypeOptions.Subscription
      ? {
          title: t('fields.billing_mode.title'),
          key: 'billing_mode',
          render: (item) => t(`fields.billing_mode.options.${item.billing_mode}`),
        }
      : null,
    requestParams.billing_type === BillingTypeOptions.Subscription
      ? {
          title: t('fields.trial_ends_at'),
          key: 'trial_ends_at',
          render: (item) =>
            item.trial_ends_at && moment(item.trial_ends_at).isAfter(moment()) ? formatDate(item.trial_ends_at) : '-',
        }
      : null,
    {
      title: t('fields.last_failed_invoice_amount'),
      key: 'last_failed_invoice_amount',
      render: (item) => (item.last_failed_invoice_amount ? formatCurrency(item.last_failed_invoice_amount) : '-'),
    },
    {
      title: t('fields.failed_payments_count'),
      key: 'failed_payments_count',
      dataIndex: 'failed_payments_count',
    },
  ])
    .filter()
    .toArray();

  return (
    <div className="mt-4 bg-background-inkWhite-white_0 shadow rounded-lg">
      <div className="p-md border-b border-brand-inkGrey-grey_2">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
          <div>
            <DateRangePicker
              variant={variant_dropdown}
              defaultFilter={filter_this_month}
              initialValue={requestParams.filters}
              onChange={(value) => {
                if (isSameDateRange(requestParams.filters, value)) {
                  return;
                }

                updateRequestParam('filters.start_date', value.start_date);
                updateRequestParam('filters.end_date', value.end_date);
              }}
            />
            <CompanySelect
              id="companies"
              value={requestParams.filters.companies}
              onChange={(value) => updateRequestParam('filters.company_ids', value)}
              className="mt-sm"
              isMulti
            />
          </div>
          <div>
            <div className="flex flex-row items-center">
              <span className="font-semibold mr-sm">{t('filters.billing_type.label')}</span>
              <Radio.Group
                optionType="button"
                value={requestParams.billing_type}
                onChange={(e) => updateRequestParam('billing_type', e.target.value)}
                options={Object.values(BillingTypeOptions).map((item) => ({
                  label: t(`filters.billing_type.options.${item}`),
                  value: item,
                }))}
              />
            </div>
            <div className="flex flex-row items-center mt-sm">
              <label htmlFor="high_risk_only" className="font-semibold mr-sm my-auto">
                {t('filters.high_risk_only.label')}:
              </label>
              <Checkbox
                id="high_risk_only"
                checked={requestParams.filters.high_risk_only}
                onChange={(e) => updateRequestParam('filters.high_risk_only', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      <Table
        dataSource={data?.data}
        columns={columns}
        pagination={{
          current: data?.current_page,
          total: data?.total,
          pageSize: data?.per_page,
          className: 'pr-[5px]',
          onChange: (page) => updateRequestParam('page', page),
        }}
        loading={loading}
        className="rounded-none shadow-card bg-white overflow-hidden"
        rowKey={(item) => item.id}
      />
    </div>
  );
}
